import { useState, useEffect, use } from "react";
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";
import Home from "./Home/Home";
import Products from "./Products/Products";
import CheckOut from "./CheckOut/CheckOut";
import About from "./About/About";
import Contact from "./Contact/Contact";
import Login from "./Login/Login";
import AdminPage from "./AdminPage/AdminPage";

const defaultProducts = [
  {
    id: 1,
    name: "Cyberpunk 2077",
    price: 59.99,
    image: "https://via.placeholder.com/300x300?text=Cyberpunk+2077",
    category: "PlayStation 5",
    rating: 4.2,
    reviews: 1250,
    featured: true,
    newRelease: true,
    description:
      "An open-world, action-adventure RPG set in the dark future of Night City.",
  },
  {
    id: 2,
    name: "The Witcher 3",
    price: 39.99,
    image: "https://via.placeholder.com/300x300?text=The+Witcher+3",
    category: "PlayStation 4",
    rating: 4.8,
    reviews: 2100,
    featured: true,
    bestSeller: true,
    description:
      "A story-driven, next-generation open world role-playing game.",
  },
  {
    id: 3,
    name: "FIFA 23",
    price: 69.99,
    image: "https://via.placeholder.com/300x300?text=FIFA+23",
    category: "PlayStation 5",
    rating: 4.1,
    reviews: 890,
    featured: false,
    newRelease: true,
    description: "The latest installment in the FIFA soccer simulation series.",
  },
  {
    id: 4,
    name: "Call of Duty: Modern Warfare",
    price: 49.99,
    image: "https://via.placeholder.com/300x300?text=Call+of+Duty",
    category: "PlayStation 4",
    rating: 4.3,
    reviews: 1500,
    featured: true,
    bestSeller: true,
    description: "A first-person shooter featuring intense multiplayer action.",
  },
  {
    id: 5,
    name: "Minecraft",
    price: 29.99,
    image: "https://via.placeholder.com/300x300?text=Minecraft",
    category: "PC",
    rating: 4.7,
    reviews: 3200,
    featured: false,
    bestSeller: true,
    description: "A sandbox game about placing blocks and going on adventures.",
  },
  {
    id: 6,
    name: "Grand Theft Auto V",
    price: 39.99,
    image: "https://via.placeholder.com/300x300?text=GTA+V",
    category: "PlayStation 4",
    rating: 4.6,
    reviews: 2800,
    featured: true,
    bestSeller: true,
    description:
      "An action-adventure game set in the fictional state of San Andreas.",
  },
  {
    id: 7,
    name: "Fortnite Battle Pass",
    price: 9.99,
    image: "https://via.placeholder.com/300x300?text=Fortnite",
    category: "Nintendo Switch",
    rating: 3.9,
    reviews: 450,
    featured: false,
    newRelease: false,
    description: "Season battle pass for Fortnite with exclusive rewards.",
  },
  {
    id: 8,
    name: "Assassin's Creed Valhalla",
    price: 49.99,
    image: "https://via.placeholder.com/300x300?text=Assassins+Creed",
    category: "PlayStation 5",
    rating: 4.0,
    reviews: 980,
    featured: false,
    newRelease: false,
    description: "An action RPG set in the Viking age.",
  },
  {
    id: 9,
    name: "Hogwarts Legacy",
    price: 59.99,
    image: "https://via.placeholder.com/300x300?text=Hogwarts+Legacy",
    category: "Xbox Series X",
    rating: 4.4,
    reviews: 1650,
    featured: true,
    newRelease: true,
    description:
      "An immersive, open-world action RPG set in the world of Harry Potter.",
  },
  {
    id: 10,
    name: "Elden Ring",
    price: 59.99,
    image: "https://via.placeholder.com/300x300?text=Elden+Ring",
    category: "PlayStation 5",
    rating: 4.9,
    reviews: 3100,
    featured: true,
    bestSeller: true,
    description:
      "An action RPG adventure set within a world created by Hidetaka Miyazaki.",
  },
];

const defaultCategories = [
  { name: "All" },
  { name: "PlayStation 4" },
  { name: "PlayStation 5" },
  { name: "Nintendo Switch" },
  { name: "Xbox Series X" },
  { name: "PC" },
];

function App() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [products, setProducts] = useState(() => {
    const saved = localStorage.getItem("gamesHubProducts");
    return saved ? JSON.parse(saved) : defaultProducts;
  });
  const [orders, setOrders] = useState(() => {
    const saved = localStorage.getItem("gamesHubOrders");
    return saved ? JSON.parse(saved) : [];
  });
  const [categories, setCategories] = useState(() => {
    const saved = localStorage.getItem("gamesHubCategories");
    return saved ? JSON.parse(saved) : defaultCategories;
  });
  const [currentPage, setCurrentPage] = useState("home");
  const navigate = useNavigate();
  useEffect(() => {
    localStorage.setItem("gamesHubProducts", JSON.stringify(products));
  }, [products]);

  useEffect(() => {
    localStorage.setItem("gamesHubOrders", JSON.stringify(orders));
  }, [orders]);

  useEffect(() => {
    localStorage.setItem("gamesHubCategories", JSON.stringify(categories));
  }, [categories]);

  const addProduct = (product) => {
    setProducts([...products, { ...product, id: Date.now() }]);
  };

  const updateProduct = (id, updatedProduct) => {
    setProducts(
      products.map((p) => (p.id === id ? { ...p, ...updatedProduct } : p)),
    );
  };

  const deleteProduct = (id) => {
    setProducts(products.filter((p) => p.id !== id));
  };

  const addOrder = (order) => {
    setOrders([...orders, { ...order, id: Date.now() }]);
  };

  const updateOrder = (id, updatedOrder) => {
    setOrders(orders.map((o) => (o.id === id ? { ...o, ...updatedOrder } : o)));
  };

  const deleteOrder = (id) => {
    setOrders(orders.filter((o) => o.id !== id));
  };
  const deleteCategory = (index) => {
    if (categories[index] === "All") return; // Don't delete "All"
    setCategories(categories.filter((_, i) => i !== index));
  };

  const login = (username, password) => {
    if (username === "admin" && password === "password") {
      setIsLoggedIn(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem("userId");
    navigate("/");
  };

  const addToCart = (product) => {
    console.log("Adding to cart:", product);
    setCart((prevOrders) => {
      // 1. Check if the product is already in the list
      const existingItem = prevOrders.find(
        (item) => item.prod_id === product.prod_id,
      );

      if (existingItem) {
        // 2. If it exists, map through and increase the quantity of THAT item
        return prevOrders.map((item) =>
          item.prod_id === product.prod_id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }

      // 3. If it's a new item, add it to the array with an initial quantity of 1
      return [...prevOrders, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredProducts = products.filter((product) => product.featured);
  const newReleases = products.filter((product) => product.newRelease);
  const bestSellers = products.filter((product) => product.bestSeller);

  const total = cart.reduce((sum, item) => {
    const price = item.prod_price ? Number(item.prod_price) : 0;
    const qty = item.quantity ? Number(item.quantity) : 0;

    return sum + price * qty;
  }, 0);
  const location = useLocation();
  const pathname = location.pathname;
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Home
            cart={cart}
            setCart={setCart}
            cartOpen={cartOpen}
            setCartOpen={setCartOpen}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            location={location}
            navigate={navigate}
            isLoggedIn={isLoggedIn}
            logout={logout}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            featuredProducts={featuredProducts}
            addToCart={addToCart}
            newReleases={newReleases}
            bestSellers={bestSellers}
            filteredProducts={filteredProducts}
            setSelectedProduct={setSelectedProduct}
            total={total}
            removeFromCart={removeFromCart}
          />
        }
      />
      <Route
        path="/products"
        element={
          <Products
            navigate={navigate}
            categories={categories}
            addToCart={addToCart}
            setSelectedProduct={setSelectedProduct}
            cart={cart}
            setCartOpen={setCartOpen}
            total={total}
            removeFromCart={removeFromCart}
            pathLocation={pathname}
            defaultProducts={products}
          />
        }
      />
      <Route
        path="/checkout"
        element={
          <CheckOut
            navigate={navigate}
            cart={cart}
            total={total}
            setCart={setCart}
            addOrder={addOrder}
            pathname={location.pathname}
            removeFromCart={removeFromCart}
            setCartOpen={setCartOpen}
          />
        }
      />
      <Route
        path="/about"
        element={
          <About
            navigate={navigate}
            pathname={location.pathname}
            cart={cart}
            total={total}
            removeFromCart={removeFromCart}
            setCartOpen={setCartOpen}
          />
        }
      />
      <Route
        path="/contact"
        element={
          <Contact
            navigate={navigate}
            pathname={location.pathname}
            cart={cart}
            total={total}
            removeFromCart={removeFromCart}
            setCartOpen={setCartOpen}
          />
        }
      />
      <Route
        path="/admin-login"
        element={<Login navigate={navigate} login={login} />}
      />
      <Route
        path="/admin"
        element={
          <AdminPage
            products={products}
            deleteProduct={deleteProduct}
            orders={orders}
            updateOrder={updateOrder}
            deleteOrder={deleteOrder}
            categories={categories}
            deleteCategory={deleteCategory}
            logout={logout}
          />
        }
      />
    </Routes>
  );
}

// function PromotionalBanner() {
//   return (
//     <div className="bg-yellow-400 rounded-lg p-6 mb-8 text-center">
//       <h3 className="text-2xl font-bold text-yellow-900 mb-2">
//         🎮 Summer Sale!
//       </h3>
//       <p className="text-yellow-800 mb-4">
//         Get up to 50% off on selected games. Limited time offer!
//       </p>
//       <button className="bg-yellow-900 text-white px-6 py-2 rounded-md hover:bg-yellow-800 transition duration-200">
//         View Deals
//       </button>
//     </div>
//   );
// }

export default App;
